import { type FC, useContext, createContext, useMemo, type ReactNode, useRef } from 'react';

type Report = (chunkName: string) => void;
type ImportCaptureContextType = {
    report: Report;
};
const ImportCaptureContext = createContext<ImportCaptureContextType | null>(null);

/**
 * This is a helper context that lets us find out which dynamic modules were rendered on the server side.
 * Suspense SSR allows us to render dynamic modules on the server side, but because it's a dynamic module we
 * don't have the styles for it in the main app bundle. This context allows us to find out which dynamic modules
 * styles we need to load first before <body> parsing starts to prevent styles flickering.
 *
 * There is no need for this context on the client side because the server side already did all the work and injected
 * all <link rel="stylesheet"> tags into DOM. Injection happens on the DBL server side.
 */
export const ImportCaptureProvider: FC<{ report: Report; children: ReactNode }> = ({
    report,
    children,
}) => {
    const value = useMemo(() => ({ report }), [report]);

    return <ImportCaptureContext.Provider value={value}>{children}</ImportCaptureContext.Provider>;
};

export const useImportCaptureReport = (chunkName: string): void => {
    const context = useContext(ImportCaptureContext);
    const isReported = useRef(false);

    if (!context) {
        /**
         * Don't report on the client side because we shouldn't have the context available on the client side.
         * This is explained in the comment above.
         */
        if (typeof window !== 'undefined') {
            return;
        }

        throw new Error('useImportCaptureReport must be used within a ImportCaptureProvider');
    }

    if (!isReported.current) {
        context.report(chunkName);
        isReported.current = true;
    }
};
